import { Link } from 'react-router-dom'
import { SEO } from '@/components/seo'
import { Shield, Zap, Users, Globe, ArrowRight } from 'lucide-react'
import { buttonStyles } from '@/components/ui'

const stats = [
  { value: '1M+', label: 'Images Processed' },
  { value: '50K+', label: 'Active Users' },
  { value: '99.9%', label: 'Uptime' },
  { value: '<2s', label: 'Avg Processing Time' },
]

const values = [
  {
    icon: Shield,
    title: 'Privacy First',
    description: 'Your images are never stored or shared. All processing happens securely, and files are deleted immediately after download.',
  },
  {
    icon: Zap,
    title: 'Innovation',
    description: 'We continuously improve our algorithms to stay ahead of new watermarking technologies and detection methods.',
  },
  {
    icon: Users,
    title: 'User Focused',
    description: 'Built by creators, for creators. We understand the frustration of AI markers limiting your creative work.',
  },
  {
    icon: Globe,
    title: 'Accessibility',
    description: 'Free tier available for everyone. We believe creative tools should be accessible regardless of budget.',
  },
]

export function About() {
  return (
    <>
      <SEO
        title="About Us - AI Detector Arena"
        description="Learn about AI Detector Arena, the leading platform for comparing AI image detectors with live benchmarks and accuracy stats."
        canonical="/about"
      />

      {/* Hero */}
      <section className="section bg-gradient-to-b from-primary-50 to-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800">
              About AI Detector Arena
            </h1>
            <p className="mt-4 text-lg text-gray-600">
              We're building the most comprehensive AI detector comparison platform on the web.
              Our mission is to provide transparent benchmarks for AI image detection tools.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section bg-white -mt-8">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary-600">{stat.value}</div>
                <div className="mt-1 text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Our Story</h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-600 leading-relaxed">
                AI Detector Arena was born from a simple observation: with dozens of AI detection tools
                available, there was no reliable way to compare their accuracy and performance.
              </p>
              <p className="text-gray-600 leading-relaxed mt-4">
                As AI image generators like Midjourney, DALL-E, and Stable Diffusion became mainstream,
                platforms started implementing AI detection tools. But which detectors actually work?
                Which ones produce false positives? We set out to answer these questions.
              </p>
              <p className="text-gray-600 leading-relaxed mt-4">
                We built AI Detector Arena to solve this problem. Our platform provides real-time
                benchmarks, ELO rankings, and accuracy statistics for all major AI detection services,
                helping users make informed decisions about which tools to trust.
              </p>
              <p className="text-gray-600 leading-relaxed mt-4">
                Today, we compare dozens of AI detectors and process thousands of test images
                to provide the most accurate, up-to-date benchmark data available anywhere.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section bg-white">
        <div className="container">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-10">Our Values</h2>
          <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {values.map((value) => (
              <div key={value.title} className="card">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary-100 rounded-xl">
                    <value.icon className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{value.title}</h3>
                    <p className="mt-2 text-sm text-gray-600">{value.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Who Uses AI Detector Arena?</h2>
            <div className="space-y-4">
              <div className="card">
                <h3 className="font-semibold text-gray-800">Content Platforms</h3>
                <p className="mt-2 text-gray-600">
                  Platforms that need to detect AI-generated content use our benchmarks to choose
                  the most accurate detection tools for their moderation workflows.
                </p>
              </div>
              <div className="card">
                <h3 className="font-semibold text-gray-800">Researchers</h3>
                <p className="mt-2 text-gray-600">
                  AI researchers and academics use our data to study detector accuracy, biases,
                  and the evolving landscape of AI image generation and detection.
                </p>
              </div>
              <div className="card">
                <h3 className="font-semibold text-gray-800">Developers</h3>
                <p className="mt-2 text-gray-600">
                  Developers building applications that require AI detection use our API
                  and benchmarks to integrate the best tools for their use case.
                </p>
              </div>
              <div className="card">
                <h3 className="font-semibold text-gray-800">Educators</h3>
                <p className="mt-2 text-gray-600">
                  Teachers and institutions evaluating AI detection tools for academic integrity
                  use our platform to compare accuracy across different content types.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-primary-600">
        <div className="container text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to compare AI detectors?
          </h2>
          <p className="text-primary-100 mb-8">
            Join thousands of users comparing AI detectors every day.
          </p>
          <Link
            to="/"
            className={buttonStyles({
              variant: 'secondary',
              size: 'lg',
              className: 'bg-white text-primary-600 hover:bg-gray-100',
            })}
          >
            Try Free Now
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  )
}
